import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUpOnSquareIcon, MoonIcon } from '@heroicons/react/24/outline';
import AstraAvatar from '../concierge/AstraAvatar';
import { useAuth } from '../../context/AuthContext';
import useAssentStatus from '../../hooks/useAssentStatus';
import { useQuickTourState } from '../../services/quickTourState';
import { useAstraToday, loadAstraToday } from '../../services/astraToday';
import {
    SCHOOL_ADDED_EVENT, pushSupport, mayAsk, markAsked, turnOnPush, isIOS, isStandalone, deviceLabel,
} from '../../services/push';
import { saveAstraNotes } from '../../services/api';
import { whenOnboardingReleased } from '../../services/onboardingHold';
import { track, EVENTS } from '../../services/analytics';

// The C1 ask (founder pick, 2026-10-10): right after a useful moment (a
// school just landed on the list), about THAT school's date. Our sheet
// first; the browser's own prompt only after "Yes, tell me", so a "Not now"
// never spends the one-time browser prompt. On an iPhone in Safari the ask
// is Add to Home Screen (iPhone pushes only to Home Screen apps), with email
// as the honest fallback; the first open from the Home Screen asks again
// with the reason. Pilot students only (astra_today).

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const IOS_HOME_ASKED = 'astra_ios_home_asked_at';
export const ASK_DELAY_MS = 2500;

function dateBits(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return { short: `${MONTHS[d.getMonth()]} ${d.getDate()}`, weekday: DAYS[d.getDay()] };
}

/** The ask's words for one school (or none): exported for the tests. */
export function askWords(school, move, device = 'phone') {
    const short = school?.short || school?.name || null;
    const when = dateBits(move?.due_date);
    if (short && when) {
        return {
            title: `Want a heads-up before ${short}’s ${when.short} deadline?`,
            body: `Astra can send a note to this ${device} when a date on your list gets close, like ${when.weekday}’s.`,
        };
    }
    if (short) {
        return {
            title: `Want a heads-up when ${short}’s dates get close?`,
            body: `Astra can send a note to this ${device} when a date on your list gets close.`,
        };
    }
    return {
        title: 'Want a heads-up when a date on your list gets close?',
        body: `Astra can send a note to this ${device} when one does.`,
    };
}

function Sheet({ title, children, actions, testid, onDismiss }) {
    const box = useRef(null);
    const dismiss = useRef(onDismiss);
    dismiss.current = onDismiss;
    // A real dialog: focus moves in ONCE (a re-render must not pull a
    // keyboard user back to the first button), Escape is "Not now".
    useEffect(() => {
        box.current?.querySelector('button')?.focus();
        const onKey = (e) => { if (e.key === 'Escape') dismiss.current?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
    return (
        <div ref={box} className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center" data-testid={testid}>
            <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
            <div role="dialog" aria-modal="true" aria-label={title}
                 className="relative w-full max-w-md rounded-t-[22px] bg-white px-[18px] pb-[22px] pt-2.5 shadow-[0_-10px_30px_rgba(0,0,0,.15)] sm:rounded-[22px]">
                <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[#E0DED8]" aria-hidden="true" />
                <div className="flex items-start gap-3">
                    <AstraAvatar size={32} />
                    <h3 className="m-0 font-serif text-xl leading-tight text-[#1A2E1F]">{title}</h3>
                </div>
                {children}
                <div className="mt-4 flex items-center gap-2">{actions}</div>
            </div>
        </div>
    );
}

const Fine = () => (
    <p className="mt-2.5 flex items-start gap-1.5 text-[12.5px] text-[#5C6A60]">
        <MoonIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        At most one a day, never 9 PM to 7 AM. Mute any kind with one tap.
    </p>
);

export default function PushAsk() {
    const { currentUser } = useAuth();
    const email = currentUser?.email;
    const today = useAstraToday();
    // Never under the Terms and 13+ confirmation (#4223, found recording the
    // Home Screen tutorial): on a new device's first sign-in AssentGate opens
    // too, and two modal dialogs stacked hid the confirmation from assistive
    // tech (the ask's aria-modal) while the ask sat behind it on screen. The
    // ask waits for assent like Astra's other surfaces (#1750).
    const assented = useAssentStatus(email) === 'assented';
    // ... nor under the quick tour, or while AppLayout is still deciding
    // whether to open it on a first sign-in (#4234).
    const tourClear = useQuickTourState() === 'none';
    const mayOpen = assented && tourClear;
    const [ask, setAsk] = useState(null);     // null | {kind: 'push'|'ios_install'|'ios_home', words}
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const [where, setWhere] = useState(false);
    // One ask at a time: a batch of adds (the hero's school picks) must not
    // retitle the sheet the student is reading (review 2026-10-11).
    const pending = useRef(false);

    const open = useCallback((kind, words) => {
        setAsk({ kind, words });
        track(EVENTS.PUSH_OPTIN_SHOWN, { kind });
    }, []);

    // The useful moment: a school was just added.
    useEffect(() => {
        if (!today.enabled || !email || !mayOpen) return undefined;
        const onAdded = async (e) => {
            const support = pushSupport();
            if (pending.current || support === 'unsupported' || !mayAsk()) return;
            pending.current = true;
            try {
                const school = e.detail || {};
                // let the add confirm and its page settle first (the analysis runs
                // on; the server's list-add trigger has already written the moves)
                await new Promise((r) => { setTimeout(r, ASK_DELAY_MS); });
                // Never over /start's first read while it is still being
                // read (#5085): held, then asked once the read is on screen.
                await whenOnboardingReleased();
                const state = await loadAstraToday(email, { force: true });
                const move = (state.moves || []).find((m) => m.school?.id === school.id && m.due_date) || null;
                const named = move?.school || { id: school.id, name: school.name, short: school.short || school.name };
                if (support === 'needs_install') open('ios_install', askWords(named, move, 'iPhone'));
                else open('push', askWords(named, move, /Android|iPhone/i.test(navigator.userAgent || '') ? 'phone' : 'device'));
            } catch {
                pending.current = false;      // a failed read never strands the ask
            }
        };
        window.addEventListener(SCHOOL_ADDED_EVENT, onAdded);
        return () => window.removeEventListener(SCHOOL_ADDED_EVENT, onAdded);
    }, [today.enabled, email, mayOpen, open]);

    // iPhone, first open from the Home Screen: ask with the reason.
    useEffect(() => {
        if (!mayOpen || !today.enabled || !today.loaded || !isIOS() || !isStandalone() || pushSupport() !== 'supported' || !mayAsk()) return;
        try { if (localStorage.getItem(IOS_HOME_ASKED)) return; localStorage.setItem(IOS_HOME_ASKED, String(Date.now())); } catch { /* ask once */ }
        const move = (today.moves || []).find((m) => m.due_date && m.school) || null;
        open('ios_home', askWords(move?.school, move, 'iPhone'));
    }, [mayOpen, today.enabled, today.loaded, today.moves, open]);

    if (!ask) {
        return done ? (
            <p role="status" className="fixed inset-x-4 bottom-24 z-[60] mx-auto max-w-md rounded-2xl border border-[#CFE3D4] bg-[#F0F7F1] p-3 text-sm text-[#1A4D2E] shadow-lg"
               data-testid="push-ask-done" onClick={() => setDone(null)}>{done}</p>
        ) : null;
    }

    const close = (outcome) => {
        markAsked();
        pending.current = false;
        setAsk(null);
        setWhere(false);
        if (outcome) track(outcome === 'yes' ? EVENTS.PUSH_OPTIN_ACCEPTED : EVENTS.PUSH_OPTIN_DECLINED, { kind: ask.kind });
    };

    const yes = async () => {
        setBusy(true);
        const result = await turnOnPush(email);          // the browser prompt, inside this tap
        // Saying yes here is the student's own choice to hear from Astra on
        // this channel: a channel switched off earlier would leave the new
        // device silent (review 2026-10-11).
        if (result === 'subscribed') await saveAstraNotes(email, { channels: { push: true } });
        setBusy(false);
        track(EVENTS.PUSH_PERMISSION_RESULT, { result });
        close('yes');
        setDone(result === 'subscribed'
            ? `Done. Astra can reach this ${deviceLabel()} now. You can change it in Settings.`
            : result === 'denied'
                ? 'Notifications are off for this site. Astra will still show everything in Astra Today.'
                : 'That didn’t go through. You can turn notifications on in Settings later.');
        setTimeout(() => setDone(null), 6000);
    };

    const emailInstead = async () => {
        setBusy(true);
        const res = await saveAstraNotes(email, { channels: { email: true } });
        setBusy(false);
        close('no');
        setDone(res?.success ? 'Okay. We’ll use email for Astra’s notes instead.' : 'That didn’t go through. Please try again in Settings.');
        setTimeout(() => setDone(null), 6000);
    };

    if (ask.kind === 'ios_install') {
        return (
            <Sheet title="Get Astra’s notes on this iPhone" testid="push-ask-ios-install" onDismiss={() => close('no')}
                   actions={(
                       <>
                           <button type="button" onClick={() => { setWhere(true); track(EVENTS.PUSH_OPTIN_ACCEPTED, { kind: 'ios_install' }); }}
                                   className="min-h-[44px] flex-1 rounded-full bg-[#1A4D2E] px-[18px] text-sm font-semibold text-white">
                               Show me where
                           </button>
                           <button type="button" onClick={emailInstead} disabled={busy}
                                   className="min-h-[44px] px-2 text-sm font-medium text-[#5C5A54] underline decoration-[#CFCBC0] underline-offset-[3px]">
                               Email me instead
                           </button>
                           <button type="button" onClick={() => close('no')} disabled={busy}
                                   className="min-h-[44px] px-2 text-sm font-medium text-[#5C5A54]">
                               Not now
                           </button>
                       </>
                   )}>
                <p className="mt-2.5 text-[14.5px] text-[#2C2C2C]">
                    iPhone only lets apps on your Home Screen send notifications. Two taps and you’re set:
                </p>
                <ol className="mt-2 list-decimal pl-5 text-[14px] text-[#2C2C2C]">
                    <li>Tap Share <ArrowUpOnSquareIcon className="inline h-4 w-4 -translate-y-px text-[#1A4D2E]" aria-label="Share" /> at the bottom of Safari.</li>
                    <li>Tap <b>Add to Home Screen</b>, then open Stratia from there.</li>
                </ol>
                {where && (
                    <p className="mt-2 rounded-xl bg-[#F5FAF5] p-2.5 text-[13px] text-[#3D4A40]" data-testid="push-ask-where">
                        The Share button is the square with an arrow pointing up, in the bar at the bottom of Safari
                        (at the top on an iPad). Scroll its list to find Add to Home Screen.
                        <button type="button" onClick={() => close(null)} className="ml-1 font-semibold text-[#1A4D2E] underline">Got it</button>
                    </p>
                )}
            </Sheet>
        );
    }

    return (
        <Sheet title={ask.words.title} testid="push-ask" onDismiss={() => close('no')}
               actions={(
                   <>
                       <button type="button" onClick={yes} disabled={busy} data-testid="push-ask-yes"
                               className="min-h-[44px] flex-1 rounded-full bg-[#1A4D2E] px-[18px] text-sm font-semibold text-white disabled:opacity-60">
                           Yes, tell me
                       </button>
                       <button type="button" onClick={() => close('no')} disabled={busy} data-testid="push-ask-no"
                               className="min-h-[44px] px-2 text-sm font-medium text-[#5C5A54] underline decoration-[#CFCBC0] underline-offset-[3px]">
                           Not now
                       </button>
                   </>
               )}>
            <p className="mt-2.5 text-[14.5px] text-[#2C2C2C]">{ask.words.body}</p>
            <Fine />
        </Sheet>
    );
}
